// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/lib/api/record-conflict.ts.
// Fark: hata gövdesi İngilizce (`record_changed`, `version`, `changed_by`, `changed_at`) ve
// `lib/problem.ts`'in `ApiError`'ından okunur; zaman İstanbul duvar saatiyle yazılır.
import { DISPLAY_TIME_ZONE, formatDateOnly } from '@/lib/format'
import { messages } from '@/lib/messages'
import { ApiError, type Problem } from '@/lib/problem'

/** Eski sürümle yazma girişiminin `code` değeri (backend `core/concurrency.py` → 409). */
export const RECORD_CHANGED_CODE = 'record_changed'

/**
 * `error` bayat sürüm 409'u ise problem gövdesi, değilse `null`. Diğer 409'lar (iş kuralı çakışmaları)
 * kayıt çakışması değildir.
 */
export function recordChangedError(error: unknown): Problem | null {
  if (!(error instanceof ApiError)) return null
  if (error.status !== 409 || error.code !== RECORD_CHANGED_CODE) return null
  return error.problem
}

/** Sayıdan sonra okunan Türkçe bulunma eki: 3 → "te" (üç'te), 6 → "da" (altı'da). */
function locativeSuffix(n: number): string {
  const unit = n % 10
  if (unit !== 0) return ['', 'de', 'de', 'te', 'te', 'te', 'da', 'de', 'de', 'da'][unit]!
  const tens = (n / 10) % 10
  // 0 sıfır, 10 on, 20 yirmi, 30 otuz, 40 kırk, 50 elli
  return ['da', 'da', 'de', 'da', 'ta', 'de'][tens] ?? 'de'
}

/** "14:05'te", "09:30'da", "12:00'de": ek son okunan sayıyı izler (dakika; tam saatte saat). */
export function clockWithLocative(hours: number, minutes: number): string {
  const text = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  return `${text}'${locativeSuffix(minutes === 0 ? hours : minutes)}`
}

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

interface IstanbulParts {
  date: string
  hours: number
  minutes: number
}

/** Mutlak anı İstanbul duvar saatine böler (`Intl`, tarayıcı saat diliminden bağımsız). */
function istanbulParts(at: Date): IstanbulParts {
  const parts = partsFormatter.formatToParts(at)
  const get = (type: string) => parts.find((p) => p.type === type)?.value
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hours: Number(get('hour')),
    minutes: Number(get('minute')),
  }
}

/**
 * Çakışma uyarısının metni: "Ayşe Yılmaz 14:05'te değiştirdi". Başka bir günse tarih de yazılır
 * ("28.10.2026 14:05'te"), `changed_by` yoksa "Başka bir kullanıcı", `changed_at` yoksa saat yazılmaz.
 */
export function recordChangedMessage(
  problem: Pick<Problem, 'changed_by' | 'changed_at'>,
  now: Date = new Date(),
): string {
  const t = messages.recordConflict
  const who = problem.changed_by?.trim() || t.unknownUser
  const at = problem.changed_at ? new Date(problem.changed_at) : null
  if (!at || Number.isNaN(at.getTime())) return t.changed(who, null)
  const parts = istanbulParts(at)
  const day = parts.date === istanbulParts(now).date ? '' : `${formatDateOnly(parts.date)} `
  return t.changed(who, `${day}${clockWithLocative(parts.hours, parts.minutes)}`)
}

/**
 * Yazmanın göndereceği sürüm: kirliyken düzenlemenin başladığı (sabitlenmiş) sürüm; değilse yüklenen
 * sürüm. Bu oturumun kendi yazmalarının döndürdüğü sürümün altına inmez (liste henüz tazelenmemiş olabilir).
 */
export function resolveVersion(state: {
  dirty: boolean
  pinned: number | null
  loaded: number | null
  own: number | null
}): number {
  const base = state.dirty && state.pinned != null ? state.pinned : state.loaded
  return Math.max(base ?? 0, state.own ?? 0)
}
